"use client";

/**
 * StreamMock — animated mock of the askFinz Stream UGC video + music platform
 * for /apps/stream.
 *
 * Layout: browser chrome bar → category chip strip → featured hero card
 * (title, channel avatar, Watch + Follow buttons) → 2×3 grid of video cards
 * (coloured gradient thumbnail, channel monogram, title, views, duration pill).
 *
 * One card carries a pulsing "Live" badge. Viewer counts drift slowly over
 * time. All data is hard-coded — no network calls.
 */
import { useEffect, useRef, useState } from "react";
import {
  Play,
  Heart,
  Radio,
  Music,
  Compass,
  Upload,
  Eye,
  Clock,
} from "lucide-react";

/* ── types ── */
type Cat = { id: string; label: string; icon: typeof Play };
type Card = {
  id: string;
  gradient: string;     // Tailwind gradient classes for the thumbnail
  monogram: string;     // channel initials
  avatarBg: string;     // Tailwind bg colour class for the avatar
  channel: string;
  title: string;
  baseViews: number;
  duration: string;     // "" for live cards
  live?: boolean;
  verified?: boolean;
};

/* ── static data ── */
const CATS: Cat[] = [
  { id: "all",     label: "All",      icon: Compass },
  { id: "finance", label: "Finance",  icon: Radio },
  { id: "tech",    label: "Tech",     icon: Play },
  { id: "music",   label: "Music",    icon: Music },
  { id: "upload",  label: "Upload",   icon: Upload },
];

const FEATURED: Omit<Card, "baseViews"> & { views: number } = {
  id: "f0",
  gradient: "from-indigo-600 via-violet-600 to-purple-700",
  monogram: "AF",
  avatarBg: "bg-violet-600",
  channel: "askFinz Originals",
  title: "Markets 2026 mid-year review — where every major asset class stands",
  views: 142_800,
  duration: "38:12",
  verified: true,
};

const CARDS: Card[] = [
  {
    id: "c1",
    gradient: "from-rose-500 via-pink-600 to-fuchsia-700",
    monogram: "KR",
    avatarBg: "bg-rose-600",
    channel: "Kira Rosen",
    title: "How central bank balance sheets shape equity risk premiums",
    baseViews: 28_410,
    duration: "22:04",
  },
  {
    id: "c2",
    gradient: "from-emerald-500 via-teal-600 to-cyan-700",
    monogram: "DB",
    avatarBg: "bg-teal-600",
    channel: "Dev Bytes",
    title: "Building a real-time options pricing engine in Rust",
    baseViews: 61_230,
    duration: "45:19",
    verified: true,
  },
  {
    id: "c3",
    gradient: "from-amber-400 via-orange-500 to-red-600",
    monogram: "LV",
    avatarBg: "bg-orange-500",
    channel: "Live Desk",
    title: "Fed press conference · live Q&A",
    baseViews: 9_800,
    duration: "",
    live: true,
  },
  {
    id: "c4",
    gradient: "from-sky-500 via-blue-600 to-indigo-700",
    monogram: "MS",
    avatarBg: "bg-blue-600",
    channel: "Macro Signals",
    title: "Reading yield curves: inverted, flat, steep — what each means",
    baseViews: 34_670,
    duration: "18:55",
  },
  {
    id: "c5",
    gradient: "from-pink-400 via-rose-500 to-red-600",
    monogram: "AM",
    avatarBg: "bg-rose-500",
    channel: "Aria Morse",
    title: "Study session — lo-fi beats for focus & deep work",
    baseViews: 88_200,
    duration: "1:04:00",
  },
  {
    id: "c6",
    gradient: "from-lime-400 via-green-500 to-emerald-700",
    monogram: "OC",
    avatarBg: "bg-green-600",
    channel: "Open Charts",
    title: "AI sector valuation multiples — week 24 snapshot",
    baseViews: 19_500,
    duration: "11:48",
  },
];

function fmtViews(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return Math.round(n / 1_000) + "K";
  return String(n);
}

/* ── component ── */
export function StreamMock() {
  const [activeCat, setActiveCat] = useState("all");
  const [followed, setFollowed] = useState(false);
  const [cardViews, setCardViews] = useState(CARDS.map((c) => c.baseViews));
  const [featViews, setFeatViews] = useState(FEATURED.views);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);

  /* drift viewer counts slowly once in view */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let timer: number | null = null;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;

        const tick = () => {
          setCardViews((cur) =>
            cur.map((v, i) => {
              const drift = Math.round((Math.random() - 0.38) * 40);
              return Math.max(CARDS[i].baseViews - 300, v + drift);
            })
          );
          setFeatViews((v) => {
            const drift = Math.round((Math.random() - 0.3) * 80);
            return Math.max(FEATURED.views - 1000, v + drift);
          });
          timer = window.setTimeout(tick, 1100);
        };
        timer = window.setTimeout(tick, 800);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (timer !== null) clearTimeout(timer);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      {/* ambient glow */}
      <div
        aria-hidden
        className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl"
      />

      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">

        {/* ── Browser chrome ── */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline bg-background/60">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 flex-1 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            stream.askfinz.ai
          </span>
          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/55 font-mono">
            <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
            Live
          </span>
        </div>

        {/* ── Category chips ── */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b hairline overflow-x-auto">
          {CATS.map((c) => {
            const Icon = c.icon;
            const on = activeCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => setActiveCat(c.id)}
                className={
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-medium whitespace-nowrap transition " +
                  (on
                    ? "bg-foreground text-background"
                    : "border hairline text-foreground/65 hover:bg-foreground/5")
                }
              >
                <Icon className="h-3 w-3" />
                {c.label}
              </button>
            );
          })}
        </div>

        {/* ── Body ── */}
        <div className="p-4 space-y-5">

          {/* ── Hero featured card ── */}
          <div className="rounded-2xl border hairline overflow-hidden bg-background">
            {/* thumbnail */}
            <div
              className={
                "h-36 sm:h-44 bg-gradient-to-br " + FEATURED.gradient +
                " relative flex items-end p-3"
              }
            >
              <span className="inline-flex items-center gap-1.5 rounded-md bg-background/20 backdrop-blur-sm px-2 py-1 text-[10px] text-white font-mono">
                <Clock className="h-2.5 w-2.5" />
                {FEATURED.duration}
              </span>
              <span className="ml-auto inline-flex items-center gap-1 rounded-md bg-background/20 backdrop-blur-sm px-2 py-1 text-[10px] text-white font-mono">
                <Eye className="h-2.5 w-2.5" />
                {fmtViews(featViews)}
              </span>
            </div>
            {/* body */}
            <div className="p-3 flex items-start gap-3">
              <span
                className={
                  "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white text-[11px] font-bold " +
                  "bg-violet-600"
                }
              >
                {FEATURED.monogram}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold leading-snug text-foreground/90 line-clamp-2">
                  {FEATURED.title}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-foreground/60 font-mono">
                  {FEATURED.channel}
                  {FEATURED.verified && (
                    <svg className="h-3 w-3 text-[hsl(var(--accent))]" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1l1.8 2.4L13 4.1l-.8 3.1L14 9.5l-2.7 1.4L10 14l-2-2-2 2-1.3-3.1L2 9.5l1.8-2.3L3 4.1l3.2-.7z" />
                    </svg>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-foreground text-background px-3 py-1.5 text-[12px] font-medium hover:opacity-90 transition"
                >
                  <Play className="h-3 w-3" fill="currentColor" strokeWidth={0} />
                  Watch
                </button>
                <button
                  type="button"
                  onClick={() => setFollowed((f) => !f)}
                  className={
                    "inline-flex items-center gap-1.5 rounded-lg border hairline px-3 py-1.5 text-[12px] font-medium transition " +
                    (followed ? "bg-foreground text-background" : "hover:bg-foreground/5")
                  }
                >
                  <Heart className={"h-3 w-3 " + (followed ? "fill-current" : "")} />
                  {followed ? "Following" : "Follow"}
                </button>
              </div>
            </div>
          </div>

          {/* ── Video grid 2×3 ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
                Recommended for you
              </span>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-foreground/55 font-mono">
                <span className="h-1 w-1 rounded-full bg-[hsl(var(--accent))]" />
                AI ranked
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {CARDS.map((card, i) => (
                <VideoCard key={card.id} card={card} views={cardViews[i]} />
              ))}
            </div>
          </div>
        </div>

        {/* ── Music player bar ── */}
        <div className="border-t hairline bg-background/60 px-4 py-2.5 flex items-center gap-3">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-gradient-to-br from-pink-500 to-rose-600 shrink-0">
            <Music className="h-3 w-3 text-white" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-medium truncate text-foreground/85">
              Study session — lo-fi beats for focus &amp; deep work
            </div>
            <div className="text-[10px] font-mono text-foreground/55 truncate">Aria Morse</div>
          </div>
          <div className="flex items-center gap-2 shrink-0 text-foreground/65">
            <button type="button" className="p-1 rounded hover:bg-foreground/5" aria-label="play">
              <Play className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="text-[10px] font-mono text-foreground/45">1:04:00</div>
        </div>
      </div>

      {/* caption */}
      <div className="mt-4 flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.18em] text-foreground/65">
        <span>
          Live platform at{" "}
          <span className="font-mono text-foreground/65">stream.askfinz.ai</span>
        </span>
      </div>
    </div>
  );
}

/* ── Video card sub-component ── */
function VideoCard({ card, views }: { card: Card; views: number }) {
  return (
    <div className="rounded-xl border hairline overflow-hidden bg-background hover:hairline-strong transition-colors">
      {/* thumbnail */}
      <div className={"relative h-24 bg-gradient-to-br " + card.gradient}>
        {card.live ? (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-md bg-red-600 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            Live
          </span>
        ) : (
          <span className="absolute bottom-2 right-2 rounded bg-black/60 backdrop-blur-sm px-1.5 py-0.5 text-[9px] font-mono text-white">
            {card.duration}
          </span>
        )}
      </div>
      {/* meta */}
      <div className="p-2 flex items-start gap-2">
        <span
          className={
            "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-white text-[9px] font-bold " +
            card.avatarBg
          }
        >
          {card.monogram}
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium leading-tight text-foreground/85 line-clamp-2">
            {card.title}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-[9.5px] text-foreground/50 font-mono">
            <Eye className="h-2.5 w-2.5" />
            {fmtViews(views)}
          </div>
        </div>
      </div>
    </div>
  );
}
